import React, { useState, useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { TURNOS, POSICOES_7, PERIODS, SORTEIOS_OPTIONS, ResultRow } from "@/components/estatisticas/constants";
import SummaryCards from "@/components/estatisticas/SummaryCards";
import GruposTab from "@/components/estatisticas/GruposTab";
import DezenasTab from "@/components/estatisticas/DezenasTab";
import CentenasTab from "@/components/estatisticas/CentenasTab";
import MilharesTab from "@/components/estatisticas/MilharesTab";
import GerarJogosEstatisticas from "@/components/estatisticas/GerarJogosEstatisticas";
import PalpitesIASection from "@/components/estatisticas/PalpitesIASection";
import PalpitesHistorico from "@/components/estatisticas/PalpitesHistorico";
import PageInstructions from "@/components/PageInstructions";
import BuscadorNumeros from "@/components/estatisticas/BuscadorNumeros";
import AnaliseAvancada from "@/components/estatisticas/AnaliseAvancada";
import DisclaimerEstatistico from "@/components/legal/DisclaimerEstatistico";
import { useAnalytics } from "@/hooks/useAnalytics";

const EstatisticasPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [period, setPeriod] = useState('90');
  const [turnoFilter, setTurnoFilter] = useState('TODOS');
  const [posicaoFilter, setPosicaoFilter] = useState('TODAS');
  const [source, setSource] = useState<'rio' | 'federal'>('rio');
  const [filterMode, setFilterMode] = useState<'periodo' | 'sorteios'>('periodo');
  const [sorteiosCount, setSorteiosCount] = useState('100');
  const [allResults, setAllResults] = useState<ResultRow[]>([]);
  const { track } = useAnalytics();
  useEffect(() => { track("tool_access", { tool: "estatisticas" }); }, [track]);

  useEffect(() => {
    fetchData();
  }, [period, source, filterMode, sorteiosCount]);

  // Base combinada (Rio + Federal) usada pela Análise Avançada
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - (filterMode === 'periodo' ? parseInt(period) : 365));
        const desde = daysAgo.toISOString().split('T')[0];

        let qRio = supabase
          .from('deu_no_poste_resultados')
          .select('data, turno, posicao, milhar, grupo')
          .order('data', { ascending: false });
        let qFed = supabase
          .from('deu_no_poste_federal')
          .select('data, posicao, milhar, grupo')
          .order('data', { ascending: false });

        if (filterMode === 'periodo' && parseInt(period) < 9999) {
          qRio = qRio.gte('data', desde);
          qFed = qFed.gte('data', desde);
        } else if (filterMode === 'sorteios') {
          const limit = parseInt(sorteiosCount);
          if (limit < 9999) {
            qRio = qRio.limit(limit);
            qFed = qFed.limit(limit);
          }
        }

        const [rio, fed] = await Promise.all([qRio, qFed]);
        const mapped: ResultRow[] = [
          ...((rio.data || []) as any[]).map((r) => ({
            data: r.data,
            turno: r.turno || 'PT',
            posicao: r.posicao,
            milhar: r.milhar,
            grupo: r.grupo,
            fonte: 'rio',
          })),
          ...((fed.data || []) as any[]).map((r) => ({
            data: r.data,
            turno: 'FED',
            posicao: r.posicao,
            milhar: r.milhar,
            grupo: r.grupo,
            fonte: 'federal',
          })),
        ] as ResultRow[];
        setAllResults(mapped);
      } catch (err) {
        console.error('Erro ao buscar base combinada:', err);
      }
    };
    fetchAll();
  }, [period, filterMode, sorteiosCount]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let data: any[] = [];
      let queryError: any = null;

      if (source === 'federal') {
        let q = supabase
          .from('deu_no_poste_federal')
          .select('data, posicao, milhar, grupo')
          .order('data', { ascending: false });

        if (filterMode === 'periodo') {
          const daysAgo = new Date();
          daysAgo.setDate(daysAgo.getDate() - parseInt(period));
          q = q.gte('data', daysAgo.toISOString().split('T')[0]);
        } else {
          const limit = parseInt(sorteiosCount);
          if (limit < 9999) q = q.limit(limit);
        }

        const { data: fedData, error: fedErr } = await q;
        data = fedData || [];
        queryError = fedErr;
      } else {
        let q = supabase
          .from('deu_no_poste_resultados')
          .select('data, turno, posicao, milhar, grupo')
          .order('data', { ascending: false });

        if (filterMode === 'periodo') {
          const daysAgo = new Date();
          daysAgo.setDate(daysAgo.getDate() - parseInt(period));
          q = q.gte('data', daysAgo.toISOString().split('T')[0]);
        } else {
          const limit = parseInt(sorteiosCount);
          if (limit < 9999) q = q.limit(limit);
        }

        const { data: rioData, error: rioErr } = await q;
        data = rioData || [];
        queryError = rioErr;
      }

      if (queryError) throw queryError;

      const mapped = (data || []).map((r: any) => ({
        data: r.data,
        turno: r.turno || 'FED',
        posicao: r.posicao,
        milhar: r.milhar,
        grupo: r.grupo,
      }));

      setResults(mapped);
    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err);
    } finally {
      setLoading(false);
    }
  };

  let filtered = turnoFilter === 'TODOS'
    ? results
    : results.filter(r => r.turno === turnoFilter);

  if (posicaoFilter !== 'TODAS') {
    filtered = filtered.filter(r => r.posicao === parseInt(posicaoFilter));
  }

  const filtrosGeracao = {
    modo_recorte: filterMode,
    recorte_valor: filterMode === 'periodo' ? period : sorteiosCount,
    posicoes: posicaoFilter === 'TODAS' ? [] : [parseInt(posicaoFilter)],
    turnos: source === 'rio' && turnoFilter !== 'TODOS' ? [turnoFilter] : [],
  };

  return (
    <>
      <SEOHead
        title="Estatísticas Jogo do Bicho - Dezenas, Centenas, Milhares e Grupos Quentes"
        description="Análise estatística completa do Jogo do Bicho e Federal. Dezenas quentes, centenas mais sorteadas, milhares frequentes, grupos quentes. Dados do Deu no Poste com filtros por turno e posição."
        keywords="estatísticas jogo do bicho, dezenas quentes bicho, centenas mais sorteadas, milhares frequentes, grupos quentes, análise de frequência bicho, estatísticas deu no poste, estatísticas federal"
        canonical="/estatisticas"
      />
    <div className="container mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6 max-w-5xl">
      <PageInstructions
        title="📖 Como usar as Estatísticas"
        steps={[
          "Escolha a fonte de dados: Paratodos Rio ou Federal",
          "Selecione o período de análise (últimos 30, 60, 90 dias, etc.)",
          "Use os filtros de turno (PTM, PT, PTV, etc.) e posição (1º ao 5º prêmio)",
          "Navegue pelas abas: Grupos, Dezenas, Centenas e Milhares para ver os mais sorteados",
          "Os números em destaque são os 'quentes' — os que mais saíram no período escolhido"
        ]}
      />
      <Card className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-2 border-yellow-500/50 shadow-lg shadow-yellow-500/10">
        <CardHeader className="text-center px-3 sm:px-6">
          <CardTitle className="text-xl sm:text-2xl lg:text-3xl font-bold flex items-center justify-center gap-2 text-yellow-400">
            <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
            Estatísticas - Deu no Poste
          </CardTitle>
          <p className="text-gray-300 text-sm sm:text-base">
            Análise dos resultados coletados automaticamente
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-3">
            <span className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-yellow-500/15 border border-yellow-500/30 px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-bold text-yellow-400 tracking-wide">
              🏛️ FEDERAL
            </span>
            <span className="text-yellow-500/60 font-bold text-sm">&</span>
            <span className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-yellow-500/15 border border-yellow-500/30 px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-bold text-yellow-400 tracking-wide">
              🎯 PARATODOS RIO
            </span>
          </div>
        </CardHeader>
      </Card>

      <AnaliseAvancada
        results={allResults.length > 0 ? allResults : results}
        filtrosGeracao={filtrosGeracao}
      />

      {/* Filters */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3 items-center">
        <Select value={source} onValueChange={(v: 'rio' | 'federal') => { setSource(v); setTurnoFilter('TODOS'); }}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rio">Paratodos Rio</SelectItem>
            <SelectItem value="federal">Federal</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterMode} onValueChange={(v: 'periodo' | 'sorteios') => setFilterMode(v)}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="periodo">Por período</SelectItem>
            <SelectItem value="sorteios">Por sorteios</SelectItem>
          </SelectContent>
        </Select>

        {filterMode === 'periodo' ? (
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-full sm:w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Select value={sorteiosCount} onValueChange={setSorteiosCount}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTEIOS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Badge variant="outline" className="text-xs whitespace-nowrap">
          {results.length} registros
        </Badge>
      </div>

      {/* Turno filter (Rio only) */}
      {source === 'rio' && (
        <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
          <div className="flex gap-1 min-w-max">
            <span className="text-xs text-muted-foreground self-center mr-1 whitespace-nowrap">Turno:</span>
            {TURNOS.map(t => (
              <Button
                key={t}
                size="sm"
                variant="outline"
                onClick={() => setTurnoFilter(t)}
                className={`text-xs sm:text-sm px-2 sm:px-3 ${
                  turnoFilter === t
                    ? 'bg-yellow-400 text-black border-yellow-300 font-bold hover:bg-yellow-300 hover:text-black'
                    : 'text-yellow-200/80 border-yellow-500/30 hover:bg-yellow-500/10 hover:text-yellow-100'
                }`}
              >
                {t}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Position filter */}
      <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
        <div className="flex gap-1 min-w-max">
          <span className="text-xs text-muted-foreground self-center mr-1 whitespace-nowrap">Posição:</span>
          {POSICOES_7.map(p => (
            <Button
              key={p}
              size="sm"
              variant="outline"
              onClick={() => setPosicaoFilter(p)}
              className={`text-xs sm:text-sm px-2 sm:px-3 ${
                posicaoFilter === p
                  ? 'bg-green-500 text-black border-green-400 font-bold hover:bg-green-400 hover:text-black'
                  : 'text-yellow-200/80 border-yellow-500/30 hover:bg-yellow-500/10 hover:text-yellow-100'
              }`}
            >
              {p === 'TODAS' ? 'Todas' : `${p}º`}
            </Button>
          ))}
        </div>
      </div>


      {loading ? (
        <Card>
          <CardContent className="p-8 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Carregando estatísticas...</span>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Nenhum resultado encontrado para o período selecionado. Os dados serão coletados automaticamente a partir de agora.
          </CardContent>
        </Card>
      ) : (
        <>
          <SummaryCards filtered={filtered} />

          <BuscadorNumeros results={filtered} />

          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <GerarJogosEstatisticas filtered={filtered} source={source} filtrosGeracao={filtrosGeracao} />
            <PalpitesIASection filtered={filtered} source={source} filtrosGeracao={filtrosGeracao} />
          </div>

          <Tabs defaultValue="grupos" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-1">
              <TabsTrigger value="grupos">Grupos</TabsTrigger>
              <TabsTrigger value="dezenas">Dezenas</TabsTrigger>
              <TabsTrigger value="centenas">Centenas</TabsTrigger>
              <TabsTrigger value="milhares">Milhares</TabsTrigger>
            </TabsList>

            <TabsContent value="grupos">
              <GruposTab filtered={filtered} />
            </TabsContent>

            <TabsContent value="dezenas">
              <DezenasTab filtered={filtered} />
            </TabsContent>

            <TabsContent value="centenas">
              <CentenasTab filtered={filtered} />
            </TabsContent>

            <TabsContent value="milhares">
              <MilharesTab filtered={filtered} />
            </TabsContent>
          </Tabs>
        </>
      )}

      <PalpitesHistorico />

      <p className="text-center text-xs text-muted-foreground">
        Total de {filtered.length} registros no período • Dados coletados de ojogodobicho.com/deu_no_poste.htm
      </p>
      <DisclaimerEstatistico />
    </div>
    </>
  );
};

export default EstatisticasPage;
